'use client';

import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { SnowReport } from '@/lib/snow';
import {
  resolveWeatherLabels,
  type WeatherLabels,
} from '@/components/weather/labels';

interface SnowDepthTableProps {
  snow: SnowReport;
  labels?: WeatherLabels;
}

export default function SnowDepthTable({ snow, labels }: SnowDepthTableProps) {
  const L = resolveWeatherLabels(labels);
  const leviNoData = snow.leviNoData && snow.levi.baseDepth === 0;
  const yllasNoData = snow.yllasNoData && snow.yllas.baseDepth === 0;

  const rows = [
    {
      label: L.tableBaseDepth,
      levi: leviNoData ? L.tableNoData : `${snow.levi.baseDepth} cm`,
      yllas: yllasNoData ? L.tableNoData : `${snow.yllas.baseDepth} cm`,
      avg: `${snow.aggregate.meanBaseDepth} cm`,
    },
    {
      label: L.tableSummitDepth,
      levi: leviNoData ? L.tableNoData : `${snow.levi.summitDepth} cm`,
      yllas: yllasNoData ? L.tableNoData : `${snow.yllas.summitDepth} cm`,
      avg: `${snow.aggregate.meanSummitDepth} cm`,
    },
    {
      label: L.tableSlopesOpen,
      levi: `${snow.levi.slopesOpen}/${snow.levi.slopesTotal}`,
      yllas: `${snow.yllas.slopesOpen}/${snow.yllas.slopesTotal}`,
      avg: `${snow.aggregate.totalSlopesOpen}/${snow.aggregate.totalSlopesTotal}`,
    },
    {
      label: L.tableLiftsOpen,
      levi: `${snow.levi.liftsOpen}/${snow.levi.liftsTotal}`,
      yllas: `${snow.yllas.liftsOpen}/${snow.yllas.liftsTotal}`,
      avg: `${snow.aggregate.totalLiftsOpen}/${snow.aggregate.totalLiftsTotal}`,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={cn(
        'overflow-hidden rounded-xl transition-colors duration-300',
        'bg-white shadow-sm',
      )}
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] text-sm">
          <thead>
            <tr
              className={cn(
                'border-b',
                'border-gray-200',
              )}
            >
              <th className="px-5 py-3 text-left font-medium text-[#9B7B3A]" />
              <th className="px-5 py-3 text-right font-medium text-[#9B7B3A]">Levi</th>
              <th className="px-5 py-3 text-right font-medium text-[#9B7B3A]">Ylläs</th>
              <th className="px-5 py-3 text-right font-medium text-[#9B7B3A]">
                {L.tableAverage}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={row.label}
                className={cn(
                  i % 2 === 1 ? 'bg-[#F0EDE5]/50' : '',
                )}
              >
                <td
                  className={cn(
                    'px-5 py-3 font-medium',
                    'text-[#1A1A2E]',
                  )}
                >
                  {row.label}
                </td>
                <td
                  className={cn(
                    'px-5 py-3 text-right tabular-nums',
                    'text-[#1A1A2E]',
                  )}
                >
                  {row.levi}
                </td>
                <td
                  className={cn(
                    'px-5 py-3 text-right tabular-nums',
                    'text-[#1A1A2E]',
                  )}
                >
                  {row.yllas}
                </td>
                <td
                  className={cn(
                    'px-5 py-3 text-right font-semibold tabular-nums',
                    'text-[#1A1A2E]',
                  )}
                >
                  {row.avg}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
}
